import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { BallotStyles, getBallotThemeStyle } from "./ballotStyles";
import { persistRegistration, type RegistrationPayload } from "./RegistrationForm";
import { checkIBAN, ibanErrorMessage } from "./iban";
import { LegalFooter } from "./LegalText";

type SepaFormData = {
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  elternteilName: string;
  strasse: string;
  plz: string;
  ort: string;
  iban: string;
  email: string;
  telefon: string;
  einwilligung: boolean;
};

// Ergebnis der DB-Mandatsprüfung (RPC sepa_mandat_lookup). Enthält bewusst
// KEINE IBAN – nur nicht-sensible Felder zum Vorausfüllen.
type MandatLookup = {
  hatMandat?: boolean;
  vorname?: string | null;
  nachname?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  email?: string | null;
  telefon?: string | null;
  istKind?: boolean | null;
  elternteilName?: string | null;
};

// Alter aus ISO-Geburtsdatum (YYYY-MM-DD) berechnen
function computeAge(iso: string): number | null {
  if (!iso) return null;
  const b = new Date(iso);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}

const HEUTE_ISO = new Date().toISOString().split("T")[0];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatIban(value: string): string {
  const cleaned = value.replace(/\s/g, "").toUpperCase();
  return cleaned.replace(/(.{4})/g, "$1 ").trim();
}

function generateMandatsreferenz(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `SEPA-${year}${month}${day}-${random}`;
}

const DEFAULT_ACCOUNT_ID = "9168a8e1-d237-4316-90fe-f0e7dfb665b9";

type SepaFormProps = {
  anlage?: "Wedding" | "Britz";
  initialData?: { name?: string; email?: string };
  registrationPayload?: RegistrationPayload;
  headerNote?: React.ReactNode;
};

export default function SepaForm({ anlage = "Wedding", initialData, registrationPayload, headerNote }: SepaFormProps) {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("a") || DEFAULT_ACCOUNT_ID;

  const [formData, setFormData] = useState<SepaFormData>({
    vorname: registrationPayload?.trainee_vorname ?? "",
    nachname: registrationPayload?.trainee_nachname ?? "",
    geburtsdatum: registrationPayload?.geburtsdatum ?? "",
    elternteilName: registrationPayload?.abweichende_kontaktperson
      ? `${registrationPayload.kontakt_vorname} ${registrationPayload.kontakt_nachname}`.trim()
      : "",
    strasse: registrationPayload?.kontakt_strasse ?? "",
    plz: registrationPayload?.kontakt_plz ?? "",
    ort: registrationPayload?.kontakt_ort ?? "",
    iban: "",
    email: registrationPayload?.email ?? initialData?.email ?? "",
    telefon: registrationPayload?.telefon ?? "",
    einwilligung: false,
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // DB-Mandatsprüfung: null = noch nicht geprüft, true/false = Ergebnis.
  const [mandatVorhanden, setMandatVorhanden] = useState<boolean | null>(null);
  // Merkt, ob beim Absenden ein bestehendes Mandat genutzt wurde (für den Erfolgs-Screen).
  const [mandatWarVorhanden, setMandatWarVorhanden] = useState(false);
  const lookupReqRef = useRef(0);

  // Namens-Autocomplete (Vorschläge aus Spieler-/Mandatsdatenbank).
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [nameSuggestOpen, setNameSuggestOpen] = useState(false);
  const suggestReqRef = useRef(0);
  const pickedRef = useRef(false);

  // Alter/Minderjährigkeit aus dem Geburtsdatum ableiten (ersetzt die frühere
  // "ist mein Kind"-Checkbox). Unter 18 ⇒ Rechnungsempfänger/Kontoinhaber nötig.
  const alterSepa = computeAge(formData.geburtsdatum);
  const istKind = alterSepa !== null && alterSepa >= 0 && alterSepa < 18;

  // Prüft per Name/E-Mail in der DB, ob bereits ein SEPA-Mandat vorliegt, und
  // füllt gespeicherte (nicht-sensible) Felder voraus. Fällt bei fehlender
  // sepa_mandat_lookup-RPC auf die vorhandene spontan_hat_mandat zurück.
  useEffect(() => {
    const vn = formData.vorname.trim();
    const nn = formData.nachname.trim();
    const em = formData.email.trim();
    const name = `${vn} ${nn}`.trim();
    const emailValid = EMAIL_REGEX.test(em);
    const canLookup = (vn.length >= 2 && nn.length >= 2) || emailValid;
    if (!canLookup) {
      setMandatVorhanden(null);
      return;
    }
    const my = ++lookupReqRef.current;
    const t = setTimeout(async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc("sepa_mandat_lookup", {
          p_account_id: accountId,
          p_name: name || null,
          p_email: emailValid ? em : null,
        });
        if (my !== lookupReqRef.current) return;
        if (rpcError) throw rpcError;
        const res = (data || {}) as MandatLookup;
        // Treffer nur anerkennen, wenn er auch NAMENTLICH zur eingegebenen Person
        // passt. Die RPC matcht "Name ODER E-Mail" – meldet man ein Kind mit der
        // E-Mail eines Elternteils an, würde sonst dessen fremdes Mandat gemeldet
        // und die Mandatserfassung fälschlich übersprungen (Person ohne Mandat).
        const resName = `${(res.vorname || "").trim()} ${(res.nachname || "").trim()}`.trim().toLowerCase();
        const nameMatches = resName !== "" && resName === name.toLowerCase();
        setMandatVorhanden(Boolean(res.hatMandat) && nameMatches);
        // Nur leere Felder vorausfüllen (nie IBAN, nie vom Nutzer Getipptes
        // überschreiben) – und nur, wenn der Treffer wirklich diese Person ist.
        if (nameMatches) {
          setFormData((prev) => ({
            ...prev,
            strasse: prev.strasse || res.strasse || "",
            plz: prev.plz || res.plz || "",
            ort: prev.ort || res.ort || "",
            telefon: prev.telefon || res.telefon || "",
            elternteilName: prev.elternteilName || res.elternteilName || "",
          }));
        }
      } catch {
        // Fallback: nur Boolean über die bestehende RPC – bewusst NUR per Name
        // (keine E-Mail), damit auch hier kein fremdes Mandat matchen kann.
        try {
          const { data } = await supabase.rpc("spontan_hat_mandat", {
            p_account_id: accountId,
            p_name: name || null,
            p_email: null,
          });
          if (my !== lookupReqRef.current) return;
          setMandatVorhanden(Boolean(data));
        } catch {
          if (my === lookupReqRef.current) setMandatVorhanden(null);
        }
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vorname, formData.nachname, formData.email, accountId]);

  // Namensvorschläge: sobald ein Nachname angetippt wird, passende volle Namen
  // aus der DB (spontan_spieler_suche) anbieten – kombiniert mit dem Vornamen.
  useEffect(() => {
    const q = `${formData.vorname.trim()} ${formData.nachname.trim()}`.trim();
    if (formData.nachname.trim().length < 1 || q.length < 2) {
      setNameSuggestions([]);
      setNameSuggestOpen(false);
      return;
    }
    if (pickedRef.current) {
      pickedRef.current = false;
      return;
    }
    const my = ++suggestReqRef.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await supabase.rpc("spontan_spieler_suche", { p_account_id: accountId, q });
        if (my !== suggestReqRef.current) return;
        const list = (Array.isArray(data) ? data : []) as string[];
        setNameSuggestions(list);
        setNameSuggestOpen(list.length > 0);
      } catch {
        if (my === suggestReqRef.current) setNameSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vorname, formData.nachname, accountId]);

  // Vollen Namen aus einem Vorschlag übernehmen: letztes Wort = Nachname, Rest = Vorname.
  function pickName(full: string) {
    pickedRef.current = true;
    const parts = full.trim().split(/\s+/);
    const nach = parts.length > 1 ? parts[parts.length - 1] : "";
    const vor = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
    setNameSuggestOpen(false);
    setNameSuggestions([]);
    setFormData((prev) => ({ ...prev, vorname: vor, nachname: nach }));
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "iban") {
      setFormData((prev) => ({ ...prev, [name]: formatIban(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!formData.vorname.trim()) {
      setError("Bitte geben Sie den Vornamen ein.");
      return;
    }
    if (!formData.nachname.trim()) {
      setError("Bitte geben Sie den Nachnamen ein.");
      return;
    }
    if (!formData.geburtsdatum) {
      setError("Bitte geben Sie das Geburtsdatum des Spielers ein.");
      return;
    }
    if (alterSepa === null || alterSepa < 0 || alterSepa > 100) {
      setError("Bitte geben Sie ein gültiges Geburtsdatum ein.");
      return;
    }

    // Liegt für diese Person bereits ein SEPA-Mandat vor? Dann kein neues Mandat
    // anlegen – nur (falls über die Anmeldung gekommen) die Anmeldung abschließen.
    if (mandatVorhanden === true) {
      setLoading(true);
      try {
        if (registrationPayload) {
          const { error: regError } = await persistRegistration(registrationPayload);
          if (regError) {
            setError(regError);
            return;
          }
        }
        setMandatWarVorhanden(true);
        setSuccess(true);
      } catch (err) {
        console.error("Submit error:", err);
        setError(
          "Beim Absenden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (istKind && !formData.elternteilName.trim()) {
      setError("Bitte geben Sie den Namen des Elternteils/Erziehungsberechtigten ein.");
      return;
    }
    if (!formData.strasse.trim()) {
      setError("Bitte geben Sie die Straße und Hausnummer ein.");
      return;
    }
    if (!formData.plz.trim()) {
      setError("Bitte geben Sie die Postleitzahl ein.");
      return;
    }
    if (!formData.ort.trim()) {
      setError("Bitte geben Sie den Ort ein.");
      return;
    }
    if (!formData.iban.trim()) {
      setError("Bitte geben Sie die IBAN ein.");
      return;
    }
    const ibanCheck = checkIBAN(formData.iban);
    if (!ibanCheck.valid) {
      setError(`Bitte geben Sie eine gültige IBAN ein. ${ibanErrorMessage(ibanCheck)}`);
      return;
    }
    if (!formData.email.trim()) {
      setError("Bitte geben Sie Ihre E-Mail-Adresse ein.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }
    if (!formData.telefon.trim()) {
      setError("Bitte geben Sie eine Telefonnummer ein.");
      return;
    }
    if (!formData.einwilligung) {
      setError("Bitte bestätigen Sie die Einwilligung zur SEPA-Lastschrift.");
      return;
    }

    setLoading(true);

    try {
      const mandatsreferenz = generateMandatsreferenz();
      const unterschriftsdatum = new Date().toISOString().split("T")[0];

      const { error: insertError } = await supabase
        .from("sepa_mandates")
        .insert({
          account_id: accountId,
          vorname: formData.vorname.trim(),
          nachname: formData.nachname.trim(),
          ist_kind: istKind,
          elternteil_name: istKind ? formData.elternteilName.trim() : null,
          strasse: formData.strasse.trim(),
          plz: formData.plz.trim(),
          ort: formData.ort.trim(),
          iban: formData.iban.replace(/\s/g, ""),
          email: formData.email.trim(),
          telefon: formData.telefon.trim(),
          mandatsreferenz,
          unterschriftsdatum,
          anlage: anlage,
        });

      if (insertError) {
        console.error("Insert error:", insertError);
        setError(
          "Beim Absenden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
        );
        return;
      }

      if (registrationPayload) {
        const { error: regError } = await persistRegistration(registrationPayload);
        if (regError) {
          setError(regError);
          return;
        }
      }

      setSuccess(true);
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        "Beim Absenden ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setLoading(false);
    }
  }

  const themeStyle = getBallotThemeStyle(anlage);

  if (success) {
    const kam_ueber_anmeldung = Boolean(registrationPayload);
    const metaText = mandatWarVorhanden
      ? kam_ueber_anmeldung
        ? "Anmeldung eingegangen"
        : "Mandat liegt bereits vor"
      : kam_ueber_anmeldung
      ? "Anmeldung & Mandat eingegangen"
      : "Mandat erteilt";
    const stampText = mandatWarVorhanden
      ? kam_ueber_anmeldung
        ? "✓ Eingegangen"
        : "✓ Mandat liegt vor"
      : kam_ueber_anmeldung
      ? "✓ Eingegangen"
      : "✓ Mandat erteilt";
    return (
      <div className="ballotForm" style={themeStyle}>
        <BallotStyles />
        <div className="sheet">
          <div className="meta">
            <span>{metaText}</span>
          </div>
          <div className="success-stamp">{stampText}</div>
          <h1 className="display">Vielen <em>Dank</em>.</h1>
          {mandatWarVorhanden ? (
            kam_ueber_anmeldung ? (
              <>
                <p className="intro">
                  Ihre Trainingsanmeldung wurde erfolgreich übermittelt. Für Sie liegt
                  bereits ein SEPA-Lastschriftmandat vor – Sie mussten kein neues
                  erteilen. Eine Bestätigung ist an Ihre E-Mail-Adresse unterwegs.
                </p>
                <p className="intro">
                  Wir prüfen Ihre Angaben und melden uns in Kürze mit einem
                  Trainingsvorschlag.
                </p>
              </>
            ) : (
              <p className="intro">
                Für Sie liegt bereits ein SEPA-Lastschriftmandat vor – es ist kein
                neues Mandat nötig. Ihr bestehendes Mandat gilt weiter.
              </p>
            )
          ) : kam_ueber_anmeldung ? (
            <>
              <p className="intro">
                Ihre Trainingsanmeldung und Ihr SEPA-Lastschriftmandat wurden
                erfolgreich übermittelt. Eine Bestätigung ist an Ihre E-Mail-Adresse
                unterwegs.
              </p>
              <p className="intro">
                Wir prüfen Ihre Angaben und melden uns in Kürze mit einem
                Trainingsvorschlag. Das Mandat wird erst wirksam, sobald eine
                verbindliche Trainingszeit vereinbart ist.
              </p>
            </>
          ) : (
            <p className="intro">
              Ihr SEPA-Lastschriftmandat wurde erfolgreich übermittelt. Wir melden uns
              in Kürze bei Ihnen.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ballotForm" style={themeStyle}>
      <BallotStyles />

      <div className="sheet">
        {headerNote}
        <div className="meta">
          <span>SEPA-Mandat</span>
        </div>

        <h1 className="display">
          SEPA-<em>Lastschriftmandat</em>
        </h1>
        <p className="intro">
          Erteilen Sie uns das Mandat zur Abbuchung der Trainingsgebühren. Die
          Daten werden verschlüsselt gespeichert und nicht an Dritte weitergegeben.
          Das erteilte SEPA-Lastschriftmandat wird erst wirksam, sobald zwischen
          Trainer und Spieler eine verbindliche Trainingszeit vereinbart worden ist.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="section-head">
            <span className="num">1</span>
            <span className="title">Trainierende Person (Spieler/in)</span>
          </div>
          <p className="muted" style={{ fontSize: 13, marginTop: -4, marginBottom: 12 }}>
            Wer nimmt am Training teil? Bei einem Kind tragen Sie hier den Namen des Kindes ein —
            den Rechnungsempfänger fragen wir gleich darunter ab.
          </p>

          <div className="field-row">
            <div className="field-num">01</div>
            <div className="field-body">
              <label>Vorname des Spielers<span className="req">●</span></label>
              <input
                type="text"
                name="vorname"
                value={formData.vorname}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">02</div>
            <div className="field-body">
              <label>Nachname des Spielers<span className="req">●</span></label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  name="nachname"
                  value={formData.nachname}
                  onChange={handleChange}
                  onFocus={() => { if (nameSuggestions.length > 0) setNameSuggestOpen(true); }}
                  onBlur={() => setTimeout(() => setNameSuggestOpen(false), 180)}
                  autoComplete="off"
                />
                {nameSuggestOpen && nameSuggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 30,
                      background: "#ffffff",
                      border: "1px solid #cdd6e4",
                      borderTop: "none",
                      maxHeight: 220,
                      overflowY: "auto",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                    }}
                  >
                    {nameSuggestions.map((n, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickName(n); }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "9px 12px",
                          background: "none",
                          border: "none",
                          borderBottom: "1px solid #eef1f6",
                          cursor: "pointer",
                          fontSize: 14,
                          color: "#1a2233",
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">03</div>
            <div className="field-body">
              <label>Geburtsdatum des Spielers<span className="req">●</span></label>
              <input
                type="date"
                name="geburtsdatum"
                value={formData.geburtsdatum}
                onChange={handleChange}
                max={HEUTE_ISO}
              />
              {alterSepa !== null && alterSepa >= 0 && alterSepa <= 100 && (
                <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                  Alter: {alterSepa} {alterSepa === 1 ? "Jahr" : "Jahre"}
                  {istKind ? " — der Rechnungsbetrag wird vom Elternteil / Erziehungsberechtigten übernommen." : ""}
                </p>
              )}
            </div>
          </div>

          {mandatVorhanden === true && (
            <div className="notice" style={{ marginTop: 16, borderColor: "#1a7a3a" }}>
              <div className="label" style={{ color: "#1a7a3a" }}>SEPA-Mandat</div>
              <div className="body">
                <p style={{ margin: 0 }}>
                  ✓ Für Sie liegt bereits ein SEPA-Lastschriftmandat vor. Sie müssen
                  kein neues erteilen{registrationPayload ? " – schließen Sie einfach Ihre Anmeldung ab." : "."}
                </p>
              </div>
            </div>
          )}

          {!mandatVorhanden && (
          <>
          {istKind && (
            <>
              <div className="section-head" style={{ marginTop: 16 }}>
                <span className="num">2</span>
                <span className="title">Rechnungsempfänger / Kontoinhaber</span>
              </div>
              <p className="muted" style={{ fontSize: 13, marginTop: -4, marginBottom: 12 }}>
                Bitte tragen Sie hier <strong>Ihren Namen</strong> als Elternteil bzw.
                Erziehungsberechtigter ein. Dieser Name muss mit dem Inhaber des unten
                angegebenen Kontos übereinstimmen.
              </p>
              <div className="field-row">
                <div className="field-num">04</div>
                <div className="field-body">
                  <label>Vor- und Nachname des Rechnungsempfängers<span className="req">●</span></label>
                  <input
                    type="text"
                    name="elternteilName"
                    value={formData.elternteilName}
                    onChange={handleChange}
                    placeholder="z.B. Maria Müller"
                    autoComplete="name"
                  />
                </div>
              </div>
            </>
          )}

          <div className="section-head">
            <span className="num">{istKind ? 3 : 2}</span>
            <span className="title">Anschrift {istKind ? "(des Rechnungsempfängers)" : ""}</span>
          </div>

          <div className="field-row">
            <div className="field-num">05</div>
            <div className="field-body">
              <label>Straße und Hausnummer<span className="req">●</span></label>
              <input
                type="text"
                name="strasse"
                value={formData.strasse}
                onChange={handleChange}
                autoComplete="street-address"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">06</div>
            <div className="field-body">
              <label>Postleitzahl<span className="req">●</span></label>
              <input
                type="text"
                name="plz"
                value={formData.plz}
                onChange={handleChange}
                maxLength={5}
                autoComplete="postal-code"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">07</div>
            <div className="field-body">
              <label>Ort<span className="req">●</span></label>
              <input
                type="text"
                name="ort"
                value={formData.ort}
                onChange={handleChange}
                autoComplete="address-level2"
              />
            </div>
          </div>

          <div className="section-head">
            <span className="num">{istKind ? 4 : 3}</span>
            <span className="title">Bankverbindung {istKind ? "(des Rechnungsempfängers)" : ""}</span>
          </div>

          <div className="field-row">
            <div className="field-num">08</div>
            <div className="field-body">
              <label>IBAN<span className="req">●</span></label>
              <input
                type="text"
                name="iban"
                className="iban"
                value={formData.iban}
                onChange={handleChange}
                autoComplete="off"
              />
              {formData.iban.trim() !== "" && (() => {
                const c = checkIBAN(formData.iban);
                if (c.reason === "empty" || c.reason === "incomplete") return null;
                return (
                  <p style={{
                    margin: "8px 0 0",
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: "0.78rem",
                    letterSpacing: "0.04em",
                    color: c.valid ? "#1a7a3a" : "var(--danger)",
                  }}>
                    {c.valid ? "✓ IBAN gültig" : ibanErrorMessage(c)}
                  </p>
                );
              })()}
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">09</div>
            <div className="field-body">
              <label>E-Mail<span className="req">●</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-num">10</div>
            <div className="field-body">
              <label>Telefonnummer<span className="req">●</span></label>
              <input
                type="tel"
                name="telefon"
                value={formData.telefon}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>
          </div>

          <div className="mandate-block">
            <p className="mandate-title">Einwilligung · SEPA-Lastschriftmandat</p>
            <p>
              Ich ermächtige die Tennisschule Zlatan Palazov und Artur Ivanenko GbR,
              Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich
              weise ich mein Kreditinstitut an, die von der Tennisschule Zlatan Palazov
              und Artur Ivanenko GbR auf mein Konto gezogenen Lastschriften einzulösen.
            </p>
            <p>
              Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem
              Belastungsdatum, die Erstattung des belasteten Betrages verlangen.
              Es gelten dabei die mit meinem Kreditinstitut vereinbarten
              Bedingungen.
            </p>
          </div>

          <label className="checkbox prose block" style={{ maxWidth: "62ch" }}>
            <input
              type="checkbox"
              name="einwilligung"
              checked={formData.einwilligung}
              onChange={handleChange}
            />
            <span>
              Ich erteile hiermit das SEPA-Lastschriftmandat und bestätige, dass
              ich berechtigt bin, über das angegebene Konto zu verfügen.
              <span className="req" style={{ marginLeft: 4 }}>●</span>
            </span>
          </label>
          </>
          )}

          {error && (
            <div className="error-line">
              <div className="label">Hinweis</div>
              <div>{error}</div>
            </div>
          )}

          <div className="submit-area">
            {mandatVorhanden === true ? (
              registrationPayload ? (
                <>
                  <p className="agb">
                    Es wird kein neues SEPA-Mandat erstellt – Ihr bestehendes Mandat
                    gilt weiter. Mit dem Abschluss akzeptieren Sie unsere{" "}
                    <a
                      href={anlage === "Britz" ? "/agb-britz" : "/agb"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Trainingsbedingungen und Preise
                    </a>
                    .
                  </p>
                  <button className="primary" type="submit" disabled={loading}>
                    {loading ? "Wird gesendet …" : "Anmeldung abschließen"}
                  </button>
                </>
              ) : (
                <p className="agb">
                  Für Sie liegt bereits ein SEPA-Lastschriftmandat vor – es ist nichts
                  weiter zu tun.
                </p>
              )
            ) : (
              <>
                <p className="agb">
                  Mit der Erteilung akzeptieren Sie unsere{" "}
                  <a
                    href={anlage === "Britz" ? "/agb-britz" : "/agb"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Trainingsbedingungen und Preise
                  </a>
                  . Ihre Daten werden verschlüsselt gespeichert, nur zur Abwicklung
                  der Trainingsbeiträge verwendet und nach Ende des Trainings­verhältnisses
                  gelöscht, soweit keine gesetzlichen (insbesondere steuerlichen)
                  Aufbewahrungspflichten entgegenstehen. Auskunft &amp; Löschung:{" "}
                  <a href="mailto:tennisabisz@gmail.com">tennisabisz@gmail.com</a>.
                </p>
                <button className="primary" type="submit" disabled={loading}>
                  {loading ? "Wird gesendet …" : "Mandat erteilen"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      <LegalFooter anlage={anlage} />
    </div>
  );
}
